import type { HouseholdType, Inputs } from '../calc/types';
import type { FieldErrors } from '../calc/validate';
import { resolveStages } from '../calc/pension';
import { CURRENCIES, GROWTH_PRESETS, INFLATION_PRESETS, levelFor } from '../calc/presets';
import { formatPercent, parseMoney, parseNumber } from './format';

/** One-time DOM population: currency options and the percentages on the preset chips. */
export function initForm(form: HTMLFormElement): void {
  const select = el<HTMLSelectElement>(form, '#currency');
  select.replaceChildren(
    ...CURRENCIES.map((c) => {
      const option = document.createElement('option');
      option.value = c.code;
      option.textContent = `${c.code} — ${c.label}`;
      return option;
    }),
  );

  for (const node of form.querySelectorAll<HTMLElement>('[data-preset]')) {
    const [group, level] = (node.dataset.preset ?? '').split('.') as [string, keyof typeof GROWTH_PRESETS];
    const presets = group === 'growth' ? GROWTH_PRESETS : INFLATION_PRESETS;
    const rate = presets[level];
    if (rate !== undefined) node.textContent = formatPercent(rate);
  }
}

export function readForm(form: HTMLFormElement): Inputs {
  const growthLevel = radioValue(form, 'growthLevel');
  const inflationLevel = radioValue(form, 'inflationLevel');

  return {
    currency: el<HTMLSelectElement>(form, '#currency').value,
    householdType: radioValue(form, 'householdType', 'individual') as HouseholdType,
    currentAge: parseNumber(value(form, '#currentAge')),
    retirementAge: parseNumber(value(form, '#retirementAge')),
    currentPot: parseMoney(value(form, '#currentPot')),
    stages: [
      {
        // Build has no years field — it balances out whatever the other two don't claim.
        years: 0,
        annualContribution: parseMoney(value(form, '#stage1Contribution')),
        growsWithInflation: el<HTMLInputElement>(form, '#stage1Inflation').checked,
      },
      {
        years: parseNumber(value(form, '#stage2Years')),
        annualContribution: parseMoney(value(form, '#stage2Contribution')),
        growsWithInflation: el<HTMLInputElement>(form, '#stage2Inflation').checked,
      },
      {
        years: parseNumber(value(form, '#stage3Years')),
        annualContribution: parseMoney(value(form, '#stage3Contribution')),
        growsWithInflation: el<HTMLInputElement>(form, '#stage3Inflation').checked,
      },
    ],
    growthRate: rateFor(growthLevel, GROWTH_PRESETS, value(form, '#growthRate')),
    inflationRate: rateFor(inflationLevel, INFLATION_PRESETS, value(form, '#inflationRate')),
    withdrawalRate: parseNumber(value(form, '#withdrawalRate')) / 100,
  };
}

/** Pushes state into the form — used on first load and on reset. */
export function writeForm(form: HTMLFormElement, input: Inputs): void {
  el<HTMLSelectElement>(form, '#currency').value = input.currency;
  setRadio(form, 'householdType', input.householdType);
  setValue(form, '#currentAge', String(input.currentAge));
  setValue(form, '#retirementAge', String(input.retirementAge));
  setValue(form, '#currentPot', input.currentPot ? String(input.currentPot) : '');

  const [s1, s2, s3] = input.stages;
  setValue(form, '#stage1Contribution', s1.annualContribution ? String(s1.annualContribution) : '');
  el<HTMLInputElement>(form, '#stage1Inflation').checked = s1.growsWithInflation;
  setValue(form, '#stage2Years', String(s2.years));
  setValue(form, '#stage2Contribution', s2.annualContribution ? String(s2.annualContribution) : '');
  el<HTMLInputElement>(form, '#stage2Inflation').checked = s2.growsWithInflation;
  setValue(form, '#stage3Years', String(s3.years));
  setValue(form, '#stage3Contribution', s3.annualContribution ? String(s3.annualContribution) : '');
  el<HTMLInputElement>(form, '#stage3Inflation').checked = s3.growsWithInflation;

  const growthLevel = levelFor(input.growthRate, GROWTH_PRESETS);
  const inflationLevel = levelFor(input.inflationRate, INFLATION_PRESETS);
  setRadio(form, 'growthLevel', growthLevel);
  setRadio(form, 'inflationLevel', inflationLevel);
  // Always mirror the live rate into the custom box, so switching to Custom
  // starts from what the user is currently looking at.
  setValue(form, '#growthRate', round(input.growthRate * 100));
  setValue(form, '#inflationRate', round(input.inflationRate * 100));
  setValue(form, '#withdrawalRate', round(input.withdrawalRate * 100));

  syncCustomVisibility(form);
}

/** Shows or hides the two "Custom" rate inputs to match the selected chips. */
export function syncCustomVisibility(form: HTMLFormElement): void {
  toggle(form, 'growth', radioValue(form, 'growthLevel') === 'custom');
  toggle(form, 'inflation', radioValue(form, 'inflationLevel') === 'custom');
}

/** Build has no years input of its own — it balances out the other two. */
const STAGE_FIELDS = [
  { range: 'stage1-range', years: undefined },
  { range: 'stage2-range', years: 'stage2Years' },
  { range: 'stage3-range', years: 'stage3Years' },
] as const;

/**
 * Live "Ages 30–40" captions on each stage, so the years you type are legible
 * at a glance.
 *
 * A stage that resolves to no years reads "Skipped" and is left otherwise
 * untouched. Rewriting its fields would be destructive: stage lengths are
 * carved out of one shared runway, so a stage collapses whenever another
 * currently claims everything — including mid-edit, while you're replacing
 * the very value that caused it. Validation explains an overrun instead.
 */
export function updateStageRanges(form: HTMLFormElement, input: Inputs): void {
  const resolved = resolveStages(input);
  const horizon = input.retirementAge - input.currentAge;

  resolved.forEach((stage, i) => {
    const { range, years } = STAGE_FIELDS[i]!;
    const node = form.querySelector<HTMLElement>(`#${range}`);
    const skipped = stage.end <= stage.start;
    // "Ages 47–53" reads as if 53 sits in this stage as well as the next one;
    // the range is really half-open, ending the day you turn 53.
    if (node) node.textContent = skipped ? 'Skipped' : `From age ${stage.start} to ${stage.end}`;

    // Stop the steppers walking past retirement; validation catches typed-in overruns.
    if (years && Number.isFinite(horizon)) {
      const field = form.querySelector<HTMLInputElement>(`#${years}`);
      if (field) field.max = String(Math.max(0, horizon));
    }
  });
}

export function showErrors(form: HTMLFormElement, errors: FieldErrors): void {
  const fields = [
    'currentAge',
    'retirementAge',
    'currentPot',
    'stage1Contribution',
    'stage2Years',
    'stage2Contribution',
    'stage3Years',
    'stage3Contribution',
    'growthRate',
    'inflationRate',
    'withdrawalRate',
  ] as const;
  for (const field of fields) {
    const input = form.querySelector<HTMLInputElement>(`#${field}`);
    const slot = form.querySelector<HTMLElement>(`#${field}-error`);
    const message = errors[field] ?? '';
    if (slot) slot.textContent = message;
    if (input) input.setAttribute('aria-invalid', message ? 'true' : 'false');
  }
}

/** The symbol shown inside the money inputs, pulled from Intl so it matches the locale. */
export function currencySymbol(code: string): string {
  const locale = CURRENCIES.find((c) => c.code === code)?.locale ?? 'en-GB';
  try {
    const parts = new Intl.NumberFormat(locale, { style: 'currency', currency: code }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value ?? code;
  } catch {
    return code;
  }
}

export function updateCurrencySymbols(form: HTMLFormElement, code: string): void {
  const symbol = currencySymbol(code);
  for (const node of form.querySelectorAll<HTMLElement>('[data-currency-symbol]')) {
    node.textContent = symbol;
  }
}

function rateFor(
  level: string,
  presets: Record<'low' | 'medium' | 'high', number>,
  customRaw: string,
): number {
  if (level === 'low' || level === 'medium' || level === 'high') return presets[level];
  return parseNumber(customRaw) / 100;
}

function toggle(form: HTMLFormElement, group: string, show: boolean): void {
  const node = form.querySelector<HTMLElement>(`[data-custom-for="${group}"]`);
  if (node) node.hidden = !show;
}

function radioValue(form: HTMLFormElement, name: string, fallback = 'custom'): string {
  return form.querySelector<HTMLInputElement>(`input[name="${name}"]:checked`)?.value ?? fallback;
}

function setRadio(form: HTMLFormElement, name: string, value: string): void {
  const input = form.querySelector<HTMLInputElement>(`input[name="${name}"][value="${value}"]`);
  if (input) input.checked = true;
}

function value(form: HTMLFormElement, selector: string): string {
  return form.querySelector<HTMLInputElement>(selector)?.value ?? '';
}

function setValue(form: HTMLFormElement, selector: string, next: string): void {
  const input = form.querySelector<HTMLInputElement>(selector);
  if (input) input.value = next;
}

function el<T extends Element>(form: HTMLFormElement, selector: string): T {
  const node = form.querySelector<T>(selector);
  if (!node) throw new Error(`Missing element: ${selector}`);
  return node;
}

const round = (n: number) => String(Number(n.toFixed(4)));
